// src/App.tsx
import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";

import { supabase } from "./supabaseClient";

import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import UpdatePassword from "./pages/UpdatePassword";
import UpgradePlan from "./pages/UpgradePlan";

import ModeSelect from "./pages/ModeSelect";

// ✅ Brand pages
import BrandKit from "./pages/brand/BrandKit";
import BrandCreate from "./pages/brand/BrandCreate";
import BrandStudio from "./pages/brand/BrandStudio";
import BrandSettings from "./pages/brand/BrandSettings";
import BrandProjects from "./pages/brand/BrandProjects";

// ✅ Brand — Project Detail (NUEVO)  ← IMPORTANTE
import BrandProjectDetail from "./pages/brand/BrandProjectDetail";

// ✅ Creator — Project Detail (NUEVO)
import CreatorProjectDetail from "./pages/CreatorProjectDetail";

// ✅ Layout
import AppShell from "./components/layout/AppShell";

// ✅ páginas públicas
import About from "./pages/About";
import Support from "./pages/Support";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Plans from "./pages/Plans";

// ✅ Gate único de modo
import ModeGate from "./components/guards/ModeGate";

// ✅ i18n
import { I18nProvider } from "./i18n/i18n";

/* ─────────────────────────────────────────────────────────────
   ✅ Mode storage hard-fix
───────────────────────────────────────────────────────────── */
function isModeValue(v: unknown): v is "creator" | "brand" {
  return v === "creator" || v === "brand";
}

function syncModeStorage() {
  try {
    const GLOBAL = "appcreativa_mode";

    const g = localStorage.getItem(GLOBAL);
    if (g !== null && !isModeValue(g)) localStorage.removeItem(GLOBAL);

    let legacyMode: "creator" | "brand" | null = null;
    const legacyKeys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;

      if (k.startsWith("appcreativa_mode_")) {
        legacyKeys.push(k);
        const v = localStorage.getItem(k);
        if (!legacyMode && isModeValue(v)) legacyMode = v;
      }
    }

    const g2 = localStorage.getItem(GLOBAL);
    if (!isModeValue(g2) && legacyMode) {
      localStorage.setItem(GLOBAL, legacyMode);
    }

    legacyKeys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

function ModeStorageSync() {
  const location = useLocation();

  useEffect(() => {
    syncModeStorage();
  }, [location.pathname]);

  return null;
}

/* ─────────────────────────────────────────────────────────────
   ✅ Callback Auth (Supabase)
───────────────────────────────────────────────────────────── */
function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const url = window.location.href;
        await supabase.auth.exchangeCodeForSession(url);
      } catch {
        // ignore
      }

      if (cancelled) return;

      const u = new URL(window.location.href);
      const type =
        u.searchParams.get("type") ||
        u.searchParams.get("action") ||
        u.searchParams.get("flow");

      if (type === "recovery") {
        navigate("/update-password", { replace: true });
        return;
      }

      navigate("/login?from=auth", { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white flex items-center justify-center">
      Verificando acceso…
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ✅ Guard de autenticación
───────────────────────────────────────────────────────────── */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] text-white flex items-center justify-center">
        Cargando…
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ModeStorageSync />

          <ModeGate>
            <Routes>
              {/* Públicas */}
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Plans />} />
              <Route path="/about" element={<About />} />
              <Route path="/support" element={<Support />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Auth */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Register />} />
              <Route path="/register" element={<Navigate to="/signup" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />

              {/* Selección de modo */}
              <Route
                path="/mode"
                element={
                  <RequireAuth>
                    <ModeSelect />
                  </RequireAuth>
                }
              />

              {/* Brand */}
              <Route
                path="/brand"
                element={
                  <RequireAuth>
                    <AppShell mode="brand" />
                  </RequireAuth>
                }
              >
                <Route index element={<BrandStudio />} />
                <Route path="kit" element={<BrandKit />} />
                <Route path="create" element={<BrandCreate />} />
                <Route path="projects" element={<BrandProjects />} />

                {/* ✅ Detalle de proyecto BRAND (galería) */}
                <Route path="projects/:projectId" element={<BrandProjectDetail />} />

                <Route path="settings" element={<BrandSettings />} />
              </Route>

              {/* Creator */}
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <AppShell mode="creator" />
                  </RequireAuth>
                }
              >
                <Route index element={<Dashboard />} />

                {/* ✅ Detalle de proyecto CREATOR */}
                <Route path="projects/:projectId" element={<CreatorProjectDetail />} />
              </Route>

              {/* Upgrade */}
              <Route
                path="/upgrade"
                element={
                  <RequireAuth>
                    <UpgradePlan />
                  </RequireAuth>
                }
              />

              {/* 404 */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center text-white bg-black text-2xl">
                    404 | Página no encontrada
                  </div>
                }
              />
            </Routes>
          </ModeGate>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}


